import * as React from 'react';
import {usePrefersReducedMotion} from '@packages/systems/spring-hooks';

import type {HTMLAttributes, SpringTooling} from '../__SharedTypes';
import {
  SkeletonSwitchKnobView,
  SkeletonSwitchTrackView,
  SkeletonView,
} from './Skeleton.styles';
import type {ViewProps} from '../View';

export type SkeletonProps = {
  /**
   * Whether to animate the Skeleton.
   */
  shouldAnimate?: boolean;
  /**
   * Content for Skeleton.
   */
  children?: React.ReactNode;
  /**
   * Height of Skeleton.
   */
  height?: React.CSSProperties['height'];
  /**
   * Width of Skeleton.
   */
  width?: React.CSSProperties['width'];
  /**
   * Whether to round the corners of Skeleton.
   */
  isRounded?: boolean;
  /**
   * Variant of Skeleton.
   */
  variant?: 'avatar' | 'control' | 'switch' | 'text' | 'title';
} & HTMLAttributes &
  Omit<ViewProps, 'animation'> &
  SpringTooling;

/**
 * # Skeleton
 *
 * A placeholder for loading content.
 *
 * ## Usage
 *
 * ```jsx
 * import * as React from "react";
 * import {Skeleton} from "@packages/systems/spring";
 *
 * export const Example = () => {
 *   return (
 *    <>
 *      <Skeleton />
 *      <Skeleton variant="avatar" />
 *      <Skeleton variant="control" />
 *      <Skeleton variant="switch" />
 *      <Skeleton variant="text" />
 *      <Skeleton variant="title" />
 *      <Skeleton height={20} width={32} isRounded />
 *    </>
 * )};
 * ```
 *
 * @component
 */
export const Skeleton = React.forwardRef<HTMLDivElement, SkeletonProps>(
  (
    {
      shouldAnimate = true,
      height = 12,
      isRounded = false,
      variant,
      width = 128,
      ...props
    }: SkeletonProps,
    ref
  ) => {
    const reduceMotion = usePrefersReducedMotion();
    const animate = Boolean(shouldAnimate && !reduceMotion);

    if (variant === 'switch') {
      return (
        <SkeletonSwitchTrackView animation={animate} {...props}>
          <SkeletonSwitchKnobView />
        </SkeletonSwitchTrackView>
      );
    }

    return (
      <SkeletonView
        animation={animate}
        height={height}
        isRounded={isRounded}
        ref={ref}
        variant={variant}
        width={width}
        {...props}
      />
    );
  }
);

Skeleton.displayName = 'Skeleton';
Skeleton.toString = SkeletonView.toString;

export default Skeleton;
